/**
 * Parses a simple two-column numeric CSV — e.g.
 * ```
 * latency bucket,Frequency
 * 2.0,0.008188
 * 2.25,0.092246
 * ...
 *
 * CSV file can be update with metadata => datetime, row per week
 * ```
 * — into `[number, number][]` rows, the same tuple shape both `LatencyPoint`
 * and `SpreadPoint` already use. `ApiTransport` itself stays a generic HTTP
 * layer — it only decides text vs. JSON and hands a CSV response back as raw
 * text — so it's `LatencyDataService`/`SpreadDataService` that call this to
 * turn that text into rows before running their existing JSON-shaped
 * validation (`isLatencyPoint`/`isSpreadPoint`) over it.
 *
 * Real exports of this data (see `public/assets/latencydata.*.csv`) carry a
 * header row *and* a trailing free-text metadata line, plus blank lines —
 * so rather than only special-casing row 0 like a strict CSV parser would,
 * any row that isn't exactly two numbers is skipped wherever it appears.
 * Only an empty result — nothing at all usable was found — is treated as
 * failure, e.g. an HTML error page served with a `.csv` URL/content-type.
 */
export function parseNumericCsv(text: string): [number, number][] {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const rows: [number, number][] = [];
  for (const line of lines) {
    const cells = line.split(",");
    if (cells.length !== 2) continue;
    const a = Number(cells[0]);
    const b = Number(cells[1]);
    if (Number.isNaN(a) || Number.isNaN(b)) continue;
    rows.push([a, b]);
  }

  if (rows.length === 0) {
    throw new Error("No numeric rows found in CSV response");
  }
  return rows;
}

/** `content-type`/URL-based sniff for whether a response should be parsed as CSV rather than JSON. */
export function looksLikeCsv(url: string, contentType: string | null): boolean {
  if (contentType?.toLowerCase().includes("csv")) return true;
  return /\.csv(?:[?#]|$)/i.test(url);
}
